import en from "./en.json";
import de from "./de.json";

export const LANGUAGE_STORAGE_KEY = "bpf.locale";
export const DEFAULT_LANGUAGE = "en";
export const SUPPORTED_LANGUAGES = ["en", "de"];

const dictionaries = { en, de };
const sourceKeys = new Map(Object.entries(en).map(([key, value]) => [value, key]));
const listeners = new Set();

const storedLanguage = () => {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

let language = storedLanguage();

export const getLanguage = () => language;
export const getFormatLocale = () => (language === "de" ? "de-DE" : "en-GB");

export const syncDocumentLanguage = () => {
  if (typeof document !== "undefined") document.documentElement.lang = language;
};

export const setLanguage = (nextLanguage) => {
  if (!SUPPORTED_LANGUAGES.includes(nextLanguage)) return;

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
  } catch {
    // The current session still works when storage is blocked.
  }

  if (language === nextLanguage) {
    syncDocumentLanguage();
    return;
  }

  language = nextLanguage;
  syncDocumentLanguage();
  listeners.forEach((listener) => listener());
};

const onStorage = (event) => {
  if (event.key !== LANGUAGE_STORAGE_KEY && event.key !== null) return;
  const next = SUPPORTED_LANGUAGES.includes(event.newValue) ? event.newValue : DEFAULT_LANGUAGE;
  if (next === language) return;

  language = next;
  syncDocumentLanguage();
  listeners.forEach((listener) => listener());
};

export const subscribeLanguage = (listener) => {
  if (listeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }

  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
};

export const t = (key, values = {}) => {
  const message = dictionaries[language][key] ?? dictionaries[DEFAULT_LANGUAGE][key] ?? key;
  return message.replace(/\{(\d+|[A-Za-z][\w]*)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match,
  );
};


// Legacy/static UI labels can be translated at render boundaries without changing canonical values.
export const translateLabel = (label, values = {}) => {
  if (typeof label !== "string") return label;
  const key = sourceKeys.get(label);
  return key ? t(key, values) : label;
};
